import { writable } from 'svelte/store';
import { dark } from '@clerk/themes';
import { initializeAuth, getAuthState, signOut, type AuthState } from '../lib/auth';
import { withClerk } from '../lib/clerkClient';
import { ensureConvexPerson } from '../lib/convexApi';
import { liveTicketsByPersonStore } from '../lib/convexRealtime';
import type { UserTicketSummary } from '../lib/types';

/**
 * App-wide session: Clerk auth state → Convex person → live "my tickets" feed.
 * This is the single place that wires identity to data; pages only subscribe.
 */
export interface SessionState {
  authLoaded: boolean;
  signedIn: boolean;
  isAdmin: boolean;
  displayName: string | null;
  /** Clerk display name, kept so the name gate can prefill its input. */
  clerkName: string | null;
  email: string | null;
  personId: string | null;
  /** Set once the person confirmed their own name; null/undefined = show gate. */
  nameConfirmedAt: number | null;
  /** Marketing opt-in state: 'granted' hides the consent checkbox everywhere. */
  marketingConsentStatus: string | null;
  error: string | null;
}

export interface MyTicketsState {
  tickets: UserTicketSummary[];
  loading: boolean;
  error: string | null;
}

export const session = writable<SessionState>({
  authLoaded: false,
  signedIn: false,
  isAdmin: false,
  displayName: null,
  clerkName: null,
  email: null,
  personId: null,
  nameConfirmedAt: null,
  marketingConsentStatus: null,
  error: null
});

export const myTickets = writable<MyTicketsState>({ tickets: [], loading: false, error: null });

let ticketsStore: ReturnType<typeof liveTicketsByPersonStore> | null = null;
let ticketsUnsubscribe: (() => void) | null = null;
let syncToken = 0;
let started = false;

function teardownTickets() {
  ticketsUnsubscribe?.();
  ticketsUnsubscribe = null;
  ticketsStore?.dispose();
  ticketsStore = null;
  myTickets.set({ tickets: [], loading: false, error: null });
}

function attachTickets(personId: string) {
  teardownTickets();
  ticketsStore = liveTicketsByPersonStore(personId);
  ticketsUnsubscribe = ticketsStore.subscribe(state => {
    myTickets.set({ tickets: state.data ?? [], loading: state.loading, error: state.error });
  });
}

async function syncPerson(state: AuthState) {
  const runId = ++syncToken;
  myTickets.update(current => ({ ...current, loading: true }));
  try {
    const clerk = await withClerk(instance => instance);
    const user = clerk?.user ?? null;
    const nameParts = [user?.firstName, user?.lastName].filter(Boolean) as string[];
    const displayName =
      user?.fullName ??
      (nameParts.length > 0 ? nameParts.join(' ') : null) ??
      user?.username ??
      state.email ??
      'Guest';

    const person = await ensureConvexPerson({
      externalId: user?.id ?? state.userId ?? undefined,
      // Server-side, ensurePerson ignores this once the person confirmed their
      // own name — Clerk sync only writes name while unconfirmed.
      name: displayName ?? 'Guest',
      email: user?.primaryEmailAddress?.emailAddress ?? state.email ?? undefined,
      phone: user?.primaryPhoneNumber?.phoneNumber ?? undefined
    });
    if (runId !== syncToken) return;

    const personId = person?._id ?? null;
    const nameConfirmedAt =
      typeof person?.nameConfirmedAt === 'number' ? person.nameConfirmedAt : null;
    const marketingConsentStatus =
      person?.marketingConsent && typeof person.marketingConsent === 'object'
        ? ((person.marketingConsent as { status?: unknown }).status as string | undefined) ?? null
        : null;
    // Once the name is confirmed, persons.name is the truth — Clerk sync stops
    // writing it — so the header must read it back, not keep the Clerk prefill.
    // clerkName stays Clerk-derived so the name gate still prefills correctly.
    const confirmedName = typeof person?.name === 'string' ? person.name.trim() : '';
    const resolvedDisplayName =
      nameConfirmedAt !== null && confirmedName ? confirmedName : displayName;
    session.update(current => ({
      ...current,
      displayName: resolvedDisplayName,
      clerkName: displayName,
      personId,
      nameConfirmedAt,
      marketingConsentStatus,
      error: null
    }));
    if (!personId) {
      teardownTickets();
      return;
    }
    attachTickets(personId);
  } catch (error) {
    if (runId !== syncToken) return;
    console.error('Failed to sync person with Convex', error);
    session.update(current => ({ ...current, error: 'Could not load your profile. Please refresh or try again.' }));
    teardownTickets();
  }
}

export async function startSession() {
  if (started) return;
  started = true;
  try {
    await initializeAuth();
  } catch (error) {
    // Auth being unreachable must never blank the app: continue signed-out.
    console.error('Auth initialization failed', error);
    session.update(current => ({
      ...current,
      authLoaded: true,
      error: 'Sign-in is currently unavailable. Please try again in a moment.'
    }));
    return;
  }
  getAuthState().subscribe(state => {
    session.update(current => ({
      ...current,
      authLoaded: state.isLoaded,
      signedIn: state.isSignedIn,
      isAdmin: state.roles.has('admin'),
      email: state.email
    }));

    if (!state.isSignedIn) {
      syncToken++;
      session.update(current => ({
        ...current,
        personId: null,
        displayName: null,
        clerkName: null,
        nameConfirmedAt: null,
        marketingConsentStatus: null
      }));
      teardownTickets();
    } else {
      void syncPerson(state);
    }
  });
}

export async function refreshMyTickets() {
  await ticketsStore?.refresh();
}

/**
 * Reflect a just-confirmed profile into the session store without a full
 * re-sync — the name gate and settings page call this after persons:confirmName
 * succeeds so the blocking panel drops (and the header updates) immediately.
 * The confirmed name becomes the displayName so the header stops showing the
 * Clerk prefill; clerkName is left untouched (the gate still prefills from it).
 */
export function markNameConfirmed(
  nameConfirmedAt: number,
  marketingConsentStatus: string | null,
  displayName?: string
) {
  session.update(current => ({
    ...current,
    nameConfirmedAt,
    displayName: displayName ?? current.displayName,
    marketingConsentStatus: marketingConsentStatus ?? current.marketingConsentStatus
  }));
}

/** Reflect a recorded marketing consent status into the session store. */
export function markConsentRecorded(status: string) {
  session.update(current => ({ ...current, marketingConsentStatus: status }));
}

export async function endSession() {
  await signOut();
}

const clerkAppearance = {
  baseTheme: dark,
  variables: {
    colorPrimary: '#ff4d17',
    colorBackground: '#0e0e10',
    colorText: '#f2efe9',
    colorInputBackground: '#121214',
    colorInputText: '#f2efe9',
    borderRadius: '0px',
    fontFamily: 'var(--font-sans)'
  },
  elements: {
    card: {
      backgroundColor: '#0e0e10',
      border: '1px solid rgba(242, 239, 233, 0.14)',
      boxShadow: '0 18px 48px rgba(0, 0, 0, 0.45)'
    },
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    },
    closeButton: {
      backgroundColor: 'transparent',
      border: '1px solid rgba(242, 239, 233, 0.14)',
      color: '#f2efe9',
      boxShadow: 'none',
      borderRadius: '0px',
      width: '36px',
      height: '36px'
    },
    closeIcon: { color: '#f4f1e8' },
    headerTitle: { color: '#f4f1e8' },
    headerSubtitle: { color: 'rgba(244, 241, 232, 0.65)' },
    socialButtons: { backgroundColor: 'rgba(244, 241, 232, 0.04)', color: '#f4f1e8' },
    socialButtonsIconButton: { border: '1px solid rgba(244, 241, 232, 0.08)' },
    dividerLine: { backgroundColor: 'rgba(244, 241, 232, 0.12)' },
    formFieldLabel: { color: '#f4f1e8' },
    formFieldInput: {
      backgroundColor: '#121421',
      border: '1px solid rgba(244, 241, 232, 0.12)',
      color: '#f4f1e8'
    },
    footerActionText: { color: 'rgba(242, 239, 233, 0.75)' },
    footerActionLink: { color: '#ff6a3d' },
    primaryButton: {
      background: '#ff4d17',
      color: '#0a0a0b',
      boxShadow: 'none'
    },
    secondaryButton: { backgroundColor: 'rgba(242, 239, 233, 0.06)', color: '#f2efe9' }
  }
};

export function openSignIn(redirectUrl?: string) {
  void withClerk(clerk => {
    const url = redirectUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined);
    clerk.openSignIn({
      afterSignInUrl: url,
      afterSignUpUrl: url,
      appearance: clerkAppearance
    });
    return null;
  });
}
